import { Download, Loader2, Lock } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { envConfig } from "~/lib/env-validation";
import { useAuth } from "~/lib/hooks/use-auth";
import { useSubscription } from "~/lib/hooks/use-subscription";

interface ExportButtonsProps {
	ico: string;
}

export function ExportButtons({ ico }: ExportButtonsProps) {
	const baseUrl = envConfig.apiBaseUrl;
	const { session } = useAuth();
	const { canAccess, loading } = useSubscription();
	const [exporting, setExporting] = useState<"excel" | "pdf" | null>(null);

	if (loading) return null;

	const hasAccess = canAccess("financials-export");

	if (!hasAccess) {
		return (
			<div className="flex items-center gap-3">
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<Lock className="h-3.5 w-3.5" />
					Export vyžaduje plán Premium
				</span>
				<Link to="/cenik" className="btn btn-primary btn-sm">
					Upgradovat
				</Link>
			</div>
		);
	}

	async function handleExport(format: "excel" | "pdf") {
		if (!session?.access_token) {
			toast.error("Pro export se prosím znovu přihlaste.");
			return;
		}
		setExporting(format);
		try {
			const res = await fetch(`${baseUrl}/companies/${ico}/financials/export/${format}`, {
				headers: { Authorization: `Bearer ${session.access_token}` },
			});
			if (!res.ok) throw new Error("Export failed");
			const blob = await res.blob();
			const ext = format === "excel" ? "xlsx" : "pdf";
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `financials-${ico}.${ext}`;
			a.click();
			URL.revokeObjectURL(url);
			toast.success(`Export ${format === "excel" ? "do Excelu" : "do PDF"} byl stažen.`);
		} catch {
			toast.error("Export se nepodařilo stáhnout. Zkuste to prosím znovu.");
		} finally {
			setExporting(null);
		}
	}

	return (
		<div className="flex gap-2">
			<button
				type="button"
				onClick={() => handleExport("excel")}
				className="btn btn-sm btn-secondary-soft gap-2"
				disabled={exporting !== null}
				aria-busy={exporting === "excel"}
			>
				{exporting === "excel" ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Download className="h-4 w-4" />
				)}
				{exporting === "excel" ? "Stahuji…" : "Excel"}
			</button>
			<button
				type="button"
				onClick={() => handleExport("pdf")}
				className="btn btn-sm btn-secondary-soft gap-2"
				disabled={exporting !== null}
				aria-busy={exporting === "pdf"}
			>
				{exporting === "pdf" ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Download className="h-4 w-4" />
				)}
				{exporting === "pdf" ? "Stahuji…" : "PDF"}
			</button>
		</div>
	);
}
